import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Typography,
  Stack,
  Avatar,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Chip,
  Button,
  InputAdornment,
  Autocomplete,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import SearchIcon from "@mui/icons-material/Search"
import RefreshIcon from "@mui/icons-material/Refresh"
import CalculateIcon from "@mui/icons-material/Calculate"
import SaveIcon from "@mui/icons-material/Save"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import config from "../../config/app.config"
import { CODE_MAP } from "../../helpers/catalogDecoder"

const API_URL = config.APP_URL
const APP_KEY = config.APP_API_KEY
const PRODUCTS_URL = API_URL + "_table/products"

const getHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser ? authUser.session_token : ""
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Api-Key": APP_KEY,
  }
}

const apiFetch = async (url) => {
  const res = await fetch(url, { headers: getHeaders() })
  if (!res.ok) throw new Error(`GET ${url} → ${res.status}`)
  const data = await res.json()
  return data.resource || data
}

const apiPatch = async (url, body) => {
  const res = await fetch(url, {
    method: "PATCH",
    headers: getHeaders(),
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`PATCH → ${res.status}`)
  return await res.json()
}

const CostDesignerTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [searchText, setSearchText] = useState("")
  const [finishFilter, setFinishFilter] = useState("")
  const [editedRows, setEditedRows] = useState({})
  const [saving, setSaving] = useState(false)

  // Markup calculator state
  const [markupMultiplier, setMarkupMultiplier] = useState(1.24)

  const fetchProducts = useCallback(async () => {
    setLoading(true)
    try {
      const data = await apiFetch(
        `${PRODUCTS_URL}?filter=(is_configurable=true)&limit=5000&fields=id,name,sku,salesprice,b2bsalesprice,variants,product_type,cabinet_position,handle_style,category`
      )
      setProducts(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error fetching products:", err)
      onSnackbar({ open: true, message: t("Error loading products"), severity: "error" })
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchProducts()
  }, [fetchProducts])

  // ─── Stats ────────────────────────────────────────────
  const stats = useMemo(() => {
    let totalVariants = 0
    let totalWithPrice = 0
    let totalNoPrice = 0
    let finishCodes = new Set()
    let priceSum = 0
    let priceCount = 0

    for (const p of products) {
      let variants = []
      try { variants = JSON.parse(p.variants || "[]") } catch (e) { /* */ }
      totalVariants += variants.length
      for (const v of variants) {
        if (v.finish_code) finishCodes.add(v.finish_code)
        if (v.salesprice) {
          totalWithPrice++
          priceSum += Number(v.salesprice)
          priceCount++
        } else {
          totalNoPrice++
        }
      }
    }

    return {
      totalProducts: products.length,
      totalVariants,
      totalWithPrice,
      totalNoPrice,
      uniqueFinishes: finishCodes.size,
      avgPrice: priceCount > 0 ? (priceSum / priceCount).toFixed(2) : 0,
      finishCodes: [...finishCodes].sort(),
    }
  }, [products])

  // ─── Flatten variants for DataGrid ────────────────────
  const rows = useMemo(() => {
    const source = selectedProduct ? [selectedProduct] : products
    const flat = []
    let idx = 0

    for (const p of source) {
      let variants = []
      try { variants = JSON.parse(p.variants || "[]") } catch (e) { /* */ }
      for (const v of variants) {
        // Apply filters
        if (finishFilter && v.finish_code !== finishFilter) continue
        if (searchText) {
          const search = searchText.toLowerCase()
          const match =
            (v.sku || "").toLowerCase().includes(search) ||
            (v.finish_code || "").toLowerCase().includes(search) ||
            (p.name || "").toLowerCase().includes(search)
          if (!match) continue
        }

        flat.push({
          id: idx++,
          productId: p.id,
          productName: p.name,
          sku: v.sku || "",
          finish_code: v.finish_code || "",
          core_material: v.core_material || "",
          width: v.width || v.depth || "",
          depth: v.depth || "",
          height: v.height || "",
          salesprice: v.salesprice,
          b2bsalesprice: v.b2bsalesprice,
          description_gr: v.description_gr || "",
          _variantSku: v.sku,
        })
      }
    }
    return flat
  }, [products, selectedProduct, finishFilter, searchText])

  // ─── DataGrid columns ────────────────────────────────
  const columns = useMemo(
    () => [
      {
        field: "productName",
        headerName: t("Template"),
        width: 100,
        renderCell: p => (
          <Chip label={p.value} size="small" sx={{ fontWeight: 600, bgcolor: "#eff6ff", color: "#1d4ed8" }} />
        ),
      },
      {
        field: "finish_code",
        headerName: t("Finish"),
        width: 80,
        renderCell: p => {
          const info = CODE_MAP[p.value]
          return (
            <Tooltip title={info ? info.gr : p.value} arrow>
              <Chip
                label={p.value}
                size="small"
                sx={{ fontWeight: 500, bgcolor: "#f0fdf4", color: "#166534" }}
              />
            </Tooltip>
          )
        },
      },
      { field: "width", headerName: t("Width"), width: 75, type: "number" },
      { field: "depth", headerName: t("Depth"), width: 75, type: "number" },
      { field: "height", headerName: t("Height"), width: 75, type: "number" },
      {
        field: "b2bsalesprice",
        headerName: t("Standard Price"),
        width: 120,
        type: "number",
        editable: true,
        renderCell: p =>
          p.value ? (
            <Typography variant="body2" fontWeight={600}>
              €{Number(p.value).toFixed(2)}
            </Typography>
          ) : (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="NOPRICE"
              size="small"
              sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 500 }}
            />
          ),
      },
      {
        field: "salesprice",
        headerName: t("Dealer Price"),
        width: 120,
        type: "number",
        editable: true,
        renderCell: p =>
          p.value ? (
            <Typography variant="body2" fontWeight={600} color="#16a34a">
              €{Number(p.value).toFixed(2)}
            </Typography>
          ) : (
            <Chip
              icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
              label="NOPRICE"
              size="small"
              sx={{ bgcolor: "#fef3c7", color: "#92400e", fontWeight: 500 }}
            />
          ),
      },
      {
        field: "markup",
        headerName: t("Markup"),
        width: 80,
        renderCell: p => {
          const std = Number(p.row.b2bsalesprice)
          const dlr = Number(p.row.salesprice)
          if (!std || !dlr) return "-"
          const ratio = (dlr / std).toFixed(2)
          return (
            <Chip
              label={`×${ratio}`}
              size="small"
              sx={{
                fontWeight: 500,
                bgcolor: ratio === "1.24" ? "#dcfce7" : "#fef3c7",
                color: ratio === "1.24" ? "#166534" : "#92400e",
              }}
            />
          )
        },
      },
      { field: "sku", headerName: t("Vendor Code"), width: 200, flex: 1 },
    ],
    [t]
  )

  // ─── Handle inline edit ───────────────────────────────
  const handleProcessRowUpdate = useCallback(
    (newRow, oldRow) => {
      setEditedRows(prev => ({
        ...prev,
        [`${newRow.productId}_${newRow._variantSku}`]: {
          productId: newRow.productId,
          variantSku: newRow._variantSku,
          salesprice: newRow.salesprice,
          b2bsalesprice: newRow.b2bsalesprice,
        },
      }))
      return newRow
    },
    []
  )

  // ─── Save edited prices ───────────────────────────────
  const handleSave = async () => {
    const edits = Object.values(editedRows)
    if (edits.length === 0) return

    setSaving(true)
    try {
      // Group edits by product
      const byProduct = {}
      for (const e of edits) {
        if (!byProduct[e.productId]) byProduct[e.productId] = []
        byProduct[e.productId].push(e)
      }

      for (const [productId, changes] of Object.entries(byProduct)) {
        const product = products.find(p => p.id === Number(productId))
        if (!product) continue

        let variants = []
        try { variants = JSON.parse(product.variants || "[]") } catch (e) { /* */ }

        // Apply changes
        for (const change of changes) {
          const variant = variants.find(v => v.sku === change.variantSku)
          if (variant) {
            if (change.salesprice !== undefined) variant.salesprice = Number(change.salesprice) || null
            if (change.b2bsalesprice !== undefined) variant.b2bsalesprice = Number(change.b2bsalesprice) || null
          }
        }

        // Recalculate product-level min prices
        const prices = variants.filter(v => v.salesprice).map(v => Number(v.salesprice))
        const b2bPrices = variants.filter(v => v.b2bsalesprice).map(v => Number(v.b2bsalesprice))

        await apiPatch(`${PRODUCTS_URL}/${productId}`, {
          variants: JSON.stringify(variants),
          salesprice: prices.length > 0 ? String(Math.min(...prices)) : null,
          b2bsalesprice: b2bPrices.length > 0 ? String(Math.min(...b2bPrices)) : null,
        })
      }

      setEditedRows({})
      onSnackbar({ open: true, message: t("Prices updated successfully"), severity: "success" })
      fetchProducts()
    } catch (err) {
      console.error("Error saving:", err)
      onSnackbar({ open: true, message: t("Error saving prices"), severity: "error" })
    }
    setSaving(false)
  }

  // ─── Bulk Markup Recalculate ──────────────────────────
  const handleBulkRecalculate = async () => {
    if (!selectedProduct) {
      onSnackbar({ open: true, message: t("Select a product first"), severity: "warning" })
      return
    }
    if (!window.confirm(t("Recalculate all dealer prices for this product using the markup multiplier?")))
      return

    setSaving(true)
    try {
      let variants = []
      try { variants = JSON.parse(selectedProduct.variants || "[]") } catch (e) { /* */ }

      let updated = 0
      for (const v of variants) {
        if (v.b2bsalesprice) {
          v.salesprice = Math.round(Number(v.b2bsalesprice) * markupMultiplier * 100) / 100
          updated++
        }
      }

      const prices = variants.filter(v => v.salesprice).map(v => Number(v.salesprice))
      const b2bPrices = variants.filter(v => v.b2bsalesprice).map(v => Number(v.b2bsalesprice))

      await apiPatch(`${PRODUCTS_URL}/${selectedProduct.id}`, {
        variants: JSON.stringify(variants),
        salesprice: prices.length > 0 ? String(Math.min(...prices)) : null,
        b2bsalesprice: b2bPrices.length > 0 ? String(Math.min(...b2bPrices)) : null,
      })

      onSnackbar({
        open: true,
        message: `${t("Recalculated")} ${updated} ${t("variants")} (×${markupMultiplier})`,
        severity: "success",
      })
      fetchProducts()
    } catch (err) {
      console.error("Error recalculating:", err)
      onSnackbar({ open: true, message: t("Error recalculating prices"), severity: "error" })
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={32} />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#fef3c7", color: "#d97706" }}>
            <AttachMoneyIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Cost Designer")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("View and manage pricing rules for configurable products")}
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          {Object.keys(editedRows).length > 0 && (
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
              sx={{ bgcolor: "#16a34a", "&:hover": { bgcolor: "#15803d" }, textTransform: "none", borderRadius: 2 }}
            >
              {t("Save")} ({Object.keys(editedRows).length})
            </Button>
          )}
          <IconButton onClick={fetchProducts} size="small" sx={{ border: "1px solid #e2e8f0" }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      {/* Stats Cards */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2, flexWrap: "wrap" }}>
        {[
          { label: t("Products"), value: stats.totalProducts, color: "#3b82f6" },
          { label: t("Total Variants"), value: stats.totalVariants.toLocaleString(), color: "#8b5cf6" },
          { label: t("With Price"), value: stats.totalWithPrice.toLocaleString(), color: "#16a34a" },
          { label: t("No Price"), value: stats.totalNoPrice.toLocaleString(), color: "#dc2626" },
          { label: t("Finishes"), value: stats.uniqueFinishes, color: "#d97706" },
          { label: t("Avg Price"), value: `€${stats.avgPrice}`, color: "#0891b2" },
        ].map(s => (
          <Card
            key={s.label}
            elevation={0}
            sx={{ border: "1px solid #e5e7eb", borderRadius: 2, minWidth: 120, flex: 1 }}
          >
            <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
              <Typography variant="caption" color="#6b7280">
                {s.label}
              </Typography>
              <Typography variant="h6" fontWeight={700} color={s.color} sx={{ fontSize: "1.1rem" }}>
                {s.value}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }} alignItems="center">
        <Autocomplete
          options={products}
          getOptionLabel={p => `${p.name} — ${p.sku}`}
          value={selectedProduct}
          onChange={(e, val) => setSelectedProduct(val)}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label={t("Filter by Product")}
              sx={{ minWidth: 250, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}
          isOptionEqualToValue={(opt, val) => opt.id === val.id}
        />
        <Autocomplete
          options={stats.finishCodes}
          value={finishFilter || null}
          onChange={(e, val) => setFinishFilter(val || "")}
          renderInput={params => (
            <TextField
              {...params}
              size="small"
              label={t("Finish Code")}
              sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
            />
          )}
        />
        <TextField
          size="small"
          placeholder={t("Search SKU...")}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 180, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />

        <Divider orientation="vertical" flexItem />

        {/* Markup Calculator */}
        <TextField
          size="small"
          label={t("Markup")}
          type="number"
          value={markupMultiplier}
          onChange={e => setMarkupMultiplier(Number(e.target.value) || 1)}
          inputProps={{ step: 0.01, min: 1 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">×</InputAdornment>,
          }}
          sx={{ width: 110, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<CalculateIcon />}
          onClick={handleBulkRecalculate}
          disabled={!selectedProduct || saving}
          sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}
        >
          {t("Recalculate")}
        </Button>
      </Stack>

      {/* Info bar */}
      <Typography variant="caption" color="#64748b" sx={{ mb: 1, display: "block" }}>
        {t("Showing")} {rows.length.toLocaleString()} {t("variants")}
        {selectedProduct && ` ${t("for")} ${selectedProduct.name}`}
        {finishFilter && ` — ${t("Finish")}: ${finishFilter}`}
        {" — "}{t("Double-click price cells to edit")}
      </Typography>

      {/* DataGrid */}
      <Box sx={{ height: 500, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          density="compact"
          pageSize={50}
          rowsPerPageOptions={[25, 50, 100]}
          processRowUpdate={handleProcessRowUpdate}
          experimentalFeatures={{ newEditingApi: true }}
          disableSelectionOnClick
          getRowClassName={params =>
            !params.row.salesprice && !params.row.b2bsalesprice ? "noprice-row" : ""
          }
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            "& .MuiDataGrid-columnHeaders": {
              bgcolor: "#f8fafc",
              borderBottom: "2px solid #e5e7eb",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: "0.75rem",
              color: "#475569",
            },
            "& .MuiDataGrid-cell": {
              fontSize: "0.8rem",
            },
            "& .noprice-row": {
              bgcolor: "#fffbeb",
            },
            "& .MuiDataGrid-cell--editing": {
              bgcolor: "#eff6ff !important",
            },
          }}
        />
      </Box>
    </Box>
  )
}

export default CostDesignerTab
